import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Card, Col, DatePicker, Empty, Row, Spin, Statistic, Table, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";

import { API_BASE_URL } from "shared/api/config";
import "./Report.css";

const { Title, Text } = Typography;

type CountBySize = Record<string, number>;

interface MonthlySummaryResponse {
  month: string;
  totalBookings: number;
  totalPhotos: number;
  totalFrames: number;
  canvas: number;
  breakdown: {
    giftPhotosBySize: CountBySize;
    additionalPhotosBySize: CountBySize;
    frameOnlyBySize: CountBySize;
    frameAndPaperBySize: CountBySize;
    canvasBySize: CountBySize;
  };
  amount: Record<string, number | string>;
  paymentSummary: Record<string, { count: number; amount: number }>;
}

interface MonthlySummaryProps {
  month?: string;
}

const formatMoney = (value?: number | string) =>
  `${Number(String(value || 0).replace(/,/g, "")).toLocaleString()}₮`;

const parseMoneyValue = (value?: number | string) =>
  Number(String(value || 0).replace(/,/g, ""));

const MonthlySummary: React.FC<MonthlySummaryProps> = ({ month }) => {
  const [date, setDate] = useState<Dayjs>(month ? dayjs(month) : dayjs());
  const [data, setData] = useState<MonthlySummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMonthlySummary = async () => {
      setLoading(true);
      setError(null);

      try {
        const selectedMonth = date.format("YYYY-MM");
        const response = await axios.get<MonthlySummaryResponse>(
          `${API_BASE_URL}/book/monthly-summary?month=${selectedMonth}`
        );
        setData(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || "Сарын тайлан татахад алдаа гарлаа");
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlySummary();
  }, [date]);

  const breakdownRows = useMemo(() => {
    if (!data) return [];

    const allSizes = new Set<string>();
    Object.values(data.breakdown).forEach((breakdown) => {
      Object.keys(breakdown || {}).forEach((size) => allSizes.add(size));
    });

    return Array.from(allSizes)
      .sort((a, b) => a.localeCompare(b))
      .map((size) => ({
        size,
        giftPhotos: data.breakdown.giftPhotosBySize[size] || 0,
        additionalPhotos: data.breakdown.additionalPhotosBySize[size] || 0,
        frameOnly: data.breakdown.frameOnlyBySize[size] || 0,
        frameAndPaper: data.breakdown.frameAndPaperBySize[size] || 0,
        canvas: data.breakdown.canvasBySize[size] || 0,
      }));
  }, [data]);

  const paymentRows = useMemo(
    () =>
      data
        ? Object.entries(data.paymentSummary || {}).map(([type, value]) => ({
            type,
            count: value.count,
            amount: value.amount,
          }))
        : [],
    [data]
  );

  return (
    <div className="report-page">
      <div className="report-toolbar">
        <div>
          <Title level={3}>Сарын тайлан</Title>
          <Text type="secondary">{data?.month || date.format("YYYY-MM")} сарын нэгтгэл</Text>
        </div>
        <DatePicker
          picker="month"
          value={date}
          onChange={(d) => d && setDate(d)}
          allowClear={false}
          format="YYYY-MM"
        />
      </div>

      {error && <Alert message={error} type="error" showIcon />}
      {loading && <Spin />}

      {!loading && data && (
        <>
          <Row gutter={[16, 16]} className="report-stat-grid">
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Бүртгэл" value={data.totalBookings} />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Урьдчилгаа" value={parseMoneyValue(data.amount.prePay)} formatter={formatMoney} />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Үлдэгдэл" value={parseMoneyValue(data.amount.postPay)} formatter={formatMoney} />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Нийт" value={parseMoneyValue(data.amount.total)} formatter={formatMoney} />
              </Card>
            </Col>
          </Row>

          <Card title="Борлуулалтын дүн" className="report-card">
            <Table
              columns={[
                { title: "Төрөл", dataIndex: "name", key: "name" },
                {
                  title: "Дүн",
                  dataIndex: "amount",
                  key: "amount",
                  align: "right" as const,
                  render: formatMoney,
                },
              ]}
              dataSource={[
                { key: "giftPhotos", name: "Дагалдах зураг", amount: data.amount.giftPhotos },
                { key: "additionalPhotos", name: "Нэмэлт зураг", amount: data.amount.additionalPhotos },
                { key: "frameOnly", name: "Дан жааз", amount: data.amount.frameOnly },
                { key: "frameAndPaper", name: "Жааз + зураг", amount: data.amount.frameAndPaper },
                { key: "canvas", name: "Canvas", amount: data.amount.canvas },
              ]}
              pagination={false}
              size="small"
            />
          </Card>

          <Card title="Хэмжээгээр задаргаа" className="report-card">
            {breakdownRows.length ? (
              <Table
                columns={[
                  { title: "Хэмжээ", dataIndex: "size", key: "size" },
                  { title: "Дагалдах", dataIndex: "giftPhotos", key: "giftPhotos", align: "right" as const },
                  { title: "Нэмэлт", dataIndex: "additionalPhotos", key: "additionalPhotos", align: "right" as const },
                  { title: "Дан жааз", dataIndex: "frameOnly", key: "frameOnly", align: "right" as const },
                  { title: "Жааз + зураг", dataIndex: "frameAndPaper", key: "frameAndPaper", align: "right" as const },
                  { title: "Canvas", dataIndex: "canvas", key: "canvas", align: "right" as const },
                ]}
                dataSource={breakdownRows}
                pagination={false}
                rowKey="size"
                size="small"
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Мэдээлэл алга" />
            )}
          </Card>

          <Card title="Төлбөрийн хэлбэр" className="report-card">
            <Table
              columns={[
                { title: "Төрөл", dataIndex: "type", key: "type" },
                { title: "Тоо", dataIndex: "count", key: "count", align: "right" as const },
                {
                  title: "Дүн",
                  dataIndex: "amount",
                  key: "amount",
                  align: "right" as const,
                  render: formatMoney,
                },
              ]}
              dataSource={paymentRows}
              pagination={false}
              rowKey="type"
              size="small"
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default MonthlySummary;
